import { sayfaYeriMi, type KanitBaglantisi, type SayfaKunyesi, type SayfaYeri } from "@oasis/domain";

/**
 * D-074 Faz 2 (K37): belge görüntüleyicinin adresi (`/belge/:id`) ve görüntü adımları. Adres değer metni, dosya adı ya da kişi bilgisi
 * taşımaz; yalnız belge kimliği, sayfa no ve sayfa kesri. Parametreler: s (hedef sayfa), yer (okunan yerler), k (kanıt belgesi), ks, kyer.
 */

/** Adreste en çok kaç yer (fazlası atılır). */
export const YER_EN_COK = 20;
const SAYFA_EN_COK = 100_000;
const KIMLIK = /^[A-Za-z0-9_-]{1,64}$/;
const SAYI = /^(?:\d+(?:\.\d+)?|\.\d+)$/;

export interface GoruntuleyiciAramasi { s?: number; yer?: string; k?: string; ks?: number; kyer?: string }
export interface GoruntuleyiciHedefi { dokumanId: string; sayfa?: number | null; yerler?: readonly SayfaYeri[] }

/** Sayfa no: 1..100000 tamsayı (sayı ya da rakamlardan oluşan metin); değilse undefined. */
export function sayfaNoCoz(x: unknown): number | undefined {
  const n = typeof x === "number" ? x : typeof x === "string" && /^\d+$/.test(x) ? Number(x) : Number.NaN;
  return Number.isInteger(n) && n >= 1 && n <= SAYFA_EN_COK ? n : undefined;
}

/** "0.1,0.2,0.3,0.05": dört kesir, en çok 4 ondalık (sondaki sıfırsız). */
export const yerMetni = (y: SayfaYeri): string => [y.sol, y.ust, y.genislik, y.yukseklik].map((n) => String(Number(n.toFixed(4)))).join(",");

/** ";" ile ayrılmış yerler; her parça dört SAYI'ya uyan sayı olmalı ve sayfaYeriMi'den geçmeli (geçmeyen atılır); ilk YER_EN_COK. Metin değilse []. */
export function yerleriCoz(m: unknown): SayfaYeri[] {
  if (typeof m !== "string") return [];
  const yerler: SayfaYeri[] = [];
  for (const parca of m.split(";")) {
    if (yerler.length >= YER_EN_COK) break;
    const s = parca.split(",").map((x) => x.trim());
    if (s.length !== 4 || !s.every((x) => SAYI.test(x))) continue;
    const [sol, ust, genislik, yukseklik] = s.map(Number) as [number, number, number, number];
    const y = { sol, ust, genislik, yukseklik };
    if (sayfaYeriMi(y)) yerler.push(y);
  }
  return yerler;
}

/** Geçerli yerlerin ilk YER_EN_COK'u, ";" ile; hiç yoksa "". */
export const yerleriMetni = (yerler: readonly SayfaYeri[]): string => yerler.filter(sayfaYeriMi).slice(0, YER_EN_COK).map(yerMetni).join(";");

/**
 * TanStack `validateSearch`. Yalnız geçerli alanlar döner (undefined anahtar yazılmaz). `yer` `s` ister; `k` kimlik kalıbına uymalı
 * (sayı gelirse metne çevrilir); `ks` ve `kyer` `k` ister; `kyer` `ks` ister. `yer`/`kyer` normalleştirilmiş metindir (yerleriMetni).
 * TanStack aramayı JSON olarak çözer: `s=3` sayı, tamamen rakamdan oluşan `k` sayı gelir; ikisi de kabul edilir.
 */
export function goruntuleyiciAramasi(a: Record<string, unknown>): GoruntuleyiciAramasi {
  const r: GoruntuleyiciAramasi = {};
  const s = sayfaNoCoz(a.s);
  if (s !== undefined) {
    r.s = s;
    const yer = yerleriMetni(yerleriCoz(a.yer));
    if (yer) r.yer = yer;
  }
  const k = typeof a.k === "number" && Number.isFinite(a.k) ? String(a.k) : a.k;
  if (typeof k === "string" && KIMLIK.test(k)) {
    r.k = k;
    const ks = sayfaNoCoz(a.ks);
    if (ks !== undefined) {
      r.ks = ks;
      const kyer = yerleriMetni(yerleriCoz(a.kyer));
      if (kyer) r.kyer = kyer;
    }
  }
  return r;
}

/**
 * Görüntüleyici adresi. Parametre sırası s, yer, k, ks, kyer; URLSearchParams ile kodlanır ("," → %2C, ";" → %3B). Geçersiz sayfa ve
 * yer yazılmaz; yer sayfasız yazılmaz. Örnek: goruntuleyiciLinki("d1", { sayfa: 1, yerler: [{ sol: 0.1, ust: 0.2, genislik: 0.3,
 * yukseklik: 0.05 }] }) === "/belge/d1?s=1&yer=0.1%2C0.2%2C0.3%2C0.05".
 */
export function goruntuleyiciLinki(dokumanId: string, o: { sayfa?: number | null; yerler?: readonly SayfaYeri[]; karsi?: GoruntuleyiciHedefi | null } = {}): string {
  const p = new URLSearchParams();
  const ekle = (sAd: string, yAd: string, sayfa: number | null | undefined, yerler: readonly SayfaYeri[] | undefined) => {
    const s = sayfaNoCoz(sayfa);
    if (s === undefined) return;
    p.set(sAd, String(s));
    const y = yerleriMetni(yerler ?? []);
    if (y) p.set(yAd, y);
  };
  ekle("s", "yer", o.sayfa, o.yerler);
  if (o.karsi) {
    p.set("k", o.karsi.dokumanId);
    ekle("ks", "kyer", o.karsi.sayfa, o.karsi.yerler);
  }
  const q = p.toString();
  return `/belge/${encodeURIComponent(dokumanId)}${q ? `?${q}` : ""}`;
}

/** Kanıt bağlantısından (PR 5) adres; ikinci kanıt verilirse yan yana. Okuyucu motorlarının "p.N" bağlantıları bunu kullanır. */
export const kanitLinki = (k: KanitBaglantisi, karsi?: KanitBaglantisi | null): string =>
  goruntuleyiciLinki(k.dokumanId, { sayfa: k.sayfa, yerler: k.yer ? [k.yer] : [], karsi: karsi ? { dokumanId: karsi.dokumanId, sayfa: karsi.sayfa, yerler: karsi.yer ? [karsi.yer] : [] } : null });

/** Yakınlaştırma adımları (%); 100 sayfanın bölme genişliğine sığmasıdır. */
export const YAKINLIK_ADIMLARI = [50, 75, 100, 125, 150, 200, 300, 400] as const;

/** Bir sonraki (+1) ya da önceki (-1) adım; uçta aynı değer. Listede olmayan değerde yöndeki en yakın adım. */
export function yakinlas(olcek: number, yon: 1 | -1): number {
  const adimlar: readonly number[] = YAKINLIK_ADIMLARI;
  if (yon === 1) return adimlar.find((a) => a > olcek) ?? adimlar[adimlar.length - 1]!;
  return [...adimlar].reverse().find((a) => a < olcek) ?? adimlar[0]!;
}

export type Donus = 0 | 90 | 180 | 270;
/** Saat yönünde çeyrek tur. */
export const dondur = (d: Donus): Donus => ((d + 90) % 360) as Donus;

/**
 * Bölmenin hedef sayfası (B9, B11). Resim tek sayfadır; hedefi yalnız 1 geçerlidir. Sayfa sayısı okunamadıysa (`toplam` 0) hedef denetlenmez.
 * Hedef dosyada yoksa bölme 1. sayfada açılır (`disari`: "Page N is not in this file." notu). Hedef yoksa (null) 1. sayfa, not yok.
 */
export function bolmeHedefi(k: Pick<SayfaKunyesi, "gorsel" | "toplam">, hedef: number | null): { toplam: number; aktif: number; hedefGecerli: boolean; disari: boolean } {
  const toplam = k.gorsel ? 1 : k.toplam;
  const hedefGecerli = hedef !== null && (k.gorsel ? hedef === 1 : toplam === 0 || hedef <= toplam);
  return { toplam, aktif: hedefGecerli ? hedef : 1, hedefGecerli, disari: hedef !== null && !hedefGecerli };
}

/** Okunan yer çerçeveleri yalnız geçerli hedef sayfada çizilir (işaret o sayfanındır, B9); öteki sayfalarda undefined. */
export const sayfaIsaretleri = (hedef: number | null, hedefGecerli: boolean, aktif: number, yerler: readonly SayfaYeri[]): readonly SayfaYeri[] | undefined =>
  hedefGecerli && aktif === hedef ? yerler : undefined;

/** Bugünkü pencerenin `page-info` yorumu (B12): resim tek sayfadır; ne PDF ne resimse "not a PDF" hatası (`pdfDegil`; metni çağıran yazar). */
export function pencereKunyesi(b: Pick<SayfaKunyesi, "pdf" | "gorsel" | "toplam">): { gorsel: boolean; toplam: number; pdfDegil: boolean } {
  if (b.gorsel) return { gorsel: true, toplam: 1, pdfDegil: false };
  return { gorsel: false, toplam: b.toplam, pdfDegil: !b.pdf };
}
